import { DateTime } from 'luxon';
import React, { useCallback } from 'react';

import { Heading } from '@/components/typography/heading';
import { useLocalZonePreference } from '@/hooks/useLocalZonePreference';

import InfoRow from './infoRow';
import styles from './logstyles.module.scss';

type Props = {
  selectedLog: any;
};

const ModelLogDetailView = ( { selectedLog }: Props ) => {
  const [ useLocalTimeZone ] = useLocalZonePreference();

  const formatTimestamp = useCallback( ( timestamp: string | number ) => {
    if ( !timestamp ) return 'N/A';

    // Parse timestamp - if number, it's Unix timestamp in seconds; if string, assume UTC ISO string
    const dt = typeof timestamp === 'number'
      ? DateTime.fromSeconds( timestamp, { zone: 'utc' } )
      : DateTime.fromISO( timestamp, { zone: 'utc' } );

    // Convert to UTC or local based on preference
    const zonedDt = useLocalTimeZone ? dt.toLocal() : dt.toUTC();

    // Format: "Feb 12, 2026, 5:17:20.383 PM (PST)"
    return zonedDt.toFormat( 'LLL d, yyyy, h:mm:ss.SSS a (ZZZZ)' );
  }, [ useLocalTimeZone ] );

  const formatDuration = ( start: string | number, end: string | number ) => {
    if ( !start || !end ) return 'N/A';
    const startTime = typeof start === 'string' ? new Date( start ).getTime() : start * 1000;
    const endTime = typeof end === 'string' ? new Date( end ).getTime() : end * 1000;
    return `${ ( ( endTime - startTime ) / 1000 ).toFixed( 3 ) }s`;
  };

  const getStatus = () => {
    if ( selectedLog.error_string ) return 'Error';
    if ( selectedLog.raw?.status === 'success' ) return 'Success';
    return 'Completed';
  };

  return (
    <div>
      <div>
        {/* Header */}
        <div className={ styles.card }>
          <div className={ styles.header }>
            <Heading as="h2" size="md" className={ styles.title }>Model Log Details</Heading>
            <span className={ styles.statusBadge }>{getStatus()}</span>
          </div>
          <InfoRow label="Log ID" value={ selectedLog.id } />
          <InfoRow label="Start Time" value={ formatTimestamp( selectedLog.start_time ) } />
          <InfoRow label="End Time" value={ formatTimestamp( selectedLog.end_time ) } />
          <InfoRow label="Duration" value={ formatDuration( selectedLog.start_time, selectedLog.end_time ) } />
        </div>

        {/* Model Details */}
        <div className={ styles.card }>
          <Heading as="h2" size="md" className={ styles.sectionTitle }>Model Details</Heading>
          <InfoRow label="Model" value={ selectedLog.model } />
          {selectedLog.agent_id && <InfoRow label="Agent ID" value={ selectedLog.agent_id } mono />}
          <InfoRow label="Constellation Name" value={ selectedLog.constellation_name } />
          <InfoRow label="Constellation Version" value={ selectedLog.constellation_version } />
          <InfoRow label="Agent Name" value={ selectedLog.agent_name } />
        </div>

        {/* Request Details */}
        <div className={ styles.card }>
          <Heading as="h2" size="md" className={ styles.sectionTitle }>Request Details</Heading>
          <InfoRow label="Client ID" value={ selectedLog.client_id } mono />
          <InfoRow label="Request ID" value={ selectedLog.request_id } mono />
          <InfoRow label="Correlation ID" value={ selectedLog.correlation_id } mono />
        </div>

        {/* Token Usage & Cost */}
        <div className={ styles.card }>
          <Heading as="h2" size="md" className={ styles.sectionTitle }>Usage & Cost</Heading>
          <InfoRow label="Prompt Tokens" value={ selectedLog.prompt_tokens } />
          <InfoRow label="Completion Tokens" value={ selectedLog.completion_tokens } />
          <InfoRow label="Total Cost" value={ selectedLog.cost ? `$${ selectedLog.cost.toFixed( 6 ) }` : 'N/A' } />
          {selectedLog.raw?.cost_breakdown && (
            <>
              <InfoRow label="Input Cost" value={ `$${ selectedLog.raw.cost_breakdown.input_cost.toFixed( 6 ) }` } />
              <InfoRow label="Output Cost" value={ `$${ selectedLog.raw.cost_breakdown.output_cost.toFixed( 6 ) }` } />
            </>
          )}
        </div>

        {/* Messages */}
        {selectedLog.messages && selectedLog.messages.length > 0 && (
          <div className={ styles.card }>
            <Heading as="h2" size="md" className={ styles.sectionTitle }>Messages</Heading>
            <div className={ styles.jsonContainer }>
              <pre className={ styles.jsonContent }>
                {JSON.stringify( selectedLog.messages, null, 2 )}
              </pre>
            </div>
          </div>
        )}

        {/* Response Choices */}
        {selectedLog.choices && selectedLog.choices.length > 0 && (
          <div className={ styles.card }>
            <Heading as="h2" size="md" className={ styles.sectionTitle }>Response</Heading>
            <InfoRow label="Finish Reason" value={ selectedLog.choices[0].finish_reason } />
            <div className={ styles.jsonContainer }>
              <pre className={ styles.jsonContent }>
                {JSON.stringify( selectedLog.choices, null, 2 )}

              </pre>
            </div>
          </div>
        )}

        {/* Requester Metadata */}
        {selectedLog.requester_metadata && (
          <div className={ styles.card }>
            <Heading as="h2" size="md" className={ styles.sectionTitle }>Requester Metadata</Heading>
            <div className={ styles.jsonContainer }>
              <pre className={ styles.jsonContent }>
                {JSON.stringify( selectedLog.requester_metadata, null, 2 )}
              </pre>
            </div>
          </div>
        )}

        {/* Error Information */}
        {selectedLog.error_string && (
          <div className={ styles.card }>
            <Heading as="h2" size="md" className={ styles.sectionTitle }>Error Details</Heading>
            <InfoRow label="Error" value={ selectedLog.error_string } />
          </div>
        )}

        {/* Raw Response */}
        {selectedLog.raw && (
          <div className={ styles.card }>
            <Heading as="h2" size="md" className={ styles.sectionTitle }>Raw Response Data</Heading>
            <div className={ styles.jsonContainer }>
              <pre className={ styles.jsonContent }>
                {JSON.stringify( selectedLog.raw, null, 2 )}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModelLogDetailView;